import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL, API_ORIGIN } from '../../../api/client';
import {
  deleteMemory,
  deleteMemoryPhoto,
  getMemory,
  listMemories,
  updateMemory,
  uploadMemoryPhotos,
} from '../../../api/memories';
import type { MemoryAsset, MemoryListItem, MemoryRead } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';
import PullToRefresh from './PullToRefresh';

export interface Memory {
  id: string;
  title: string;
  eventDate: string;
  recordedAt: string;
  happenedAt: string;
  happenedAtFull: string;
  preview: string;
  fullText: string;
  dominantEmotion: string;
  dominantColor: string;
  emotions: { label: string; value: number; color: string; gradient: string }[];
  assets: MemoryAsset[];
}

const EMOTION_META: Record<string, { label: string; color: string }> = {
  joy: { label: 'Радость', color: '#3ECFA0' },
  sadness: { label: 'Печаль', color: '#4A90E2' },
  fear: { label: 'Страх', color: '#8B7AB8' },
  anger: { label: 'Гнев', color: '#FF6B6B' },
};

function parseDate(value: string): Date {
  return new Date(value.includes('T') ? value : `${value}T00:00:00`);
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
    .format(parseDate(value))
    .replace('.', '');
}

function formatFullDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parseDate(value));
}

function emotionData(memory: MemoryListItem | MemoryRead) {
  return [
    { label: 'Радость', value: memory.joy_score ?? 0, color: '#3ECFA0', gradient: 'linear-gradient(90deg, #3ECFA0, #2BA87F)' },
    { label: 'Печаль', value: memory.sadness_score ?? 0, color: '#4A90E2', gradient: 'linear-gradient(90deg, #4A90E2, #357ABD)' },
    { label: 'Страх', value: memory.fear_score ?? 0, color: '#8B7AB8', gradient: 'linear-gradient(90deg, #8B7AB8, #6B5A98)' },
    { label: 'Гнев', value: memory.anger_score ?? 0, color: '#FF6B6B', gradient: 'linear-gradient(90deg, #FF6B6B, #EE5A52)' },
  ];
}

function toUiMemory(memory: MemoryListItem | MemoryRead): Memory {
  const dominantKey = memory.dominant_emotion || 'joy';
  const dominant = EMOTION_META[dominantKey] || EMOTION_META.joy;
  const text = memory.story_text || (memory.status === 'completed' ? 'Текст воспоминания пока пуст.' : 'Воспоминание обрабатывается Омутом Памяти...');

  return {
    id: memory.id,
    title: memory.title || 'Новое воспоминание',
    eventDate: memory.event_date,
    recordedAt: formatShortDate(memory.recorded_at),
    happenedAt: formatShortDate(memory.event_date),
    happenedAtFull: formatFullDate(memory.event_date),
    preview: text.length > 90 ? `${text.slice(0, 90)}...` : text,
    fullText: text,
    dominantEmotion: dominant.label,
    dominantColor: dominant.color,
    emotions: emotionData(memory),
    assets: 'assets' in memory ? memory.assets : [],
  };
}

function assetUrl(asset: MemoryAsset): string {
  if (asset.url.startsWith('http')) return asset.url;
  if (asset.url.startsWith('/api/')) return `${API_ORIGIN}${asset.url}`;
  if (asset.url.startsWith('/media/')) return `${API_BASE_URL}${asset.url}`;
  return `${API_ORIGIN}${asset.url}`;
}

type View = 'list' | 'detail' | 'edit';

export default function HistoryScreen({
  refreshKey = 0,
  initialMemoryId = null,
  onInitialMemoryHandled,
}: {
  refreshKey?: number;
  initialMemoryId?: string | null;
  onInitialMemoryHandled?: () => void;
}) {
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [selected, setSelected] = useState<Memory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Memory | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Воспоминание отредактировано');

  const reload = useCallback(() => {
    if (!userId) return Promise.resolve();
    setLoading(true);
    setLoadError(null);
    return listMemories({ userId, limit: 100 })
      .then((items) => {
        const next = items.map(toUiMemory);
        setMemories(next);
        setSelected((current) => current ? next.find((memory) => memory.id === current.id) ?? null : null);
      })
      .catch((error) => {
        setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить историю');
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  const openDetail = useCallback((m: Memory) => {
    setSelected(m);
    setView('detail');
    getMemory(m.id)
      .then((memory) => {
        const uiMemory = toUiMemory(memory);
        setSelected(uiMemory);
        setMemories((prev) => prev.map((item) => (item.id === uiMemory.id ? uiMemory : item)));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!initialMemoryId || memories.length === 0) return;
    const memory = memories.find((item) => item.id === initialMemoryId);
    if (memory) {
      openDetail(memory);
      onInitialMemoryHandled?.();
    }
  }, [initialMemoryId, memories, onInitialMemoryHandled, openDetail]);

  const openEdit = () => setView('edit');
  const backToList = () => { setView('list'); setSelected(null); };
  const backToDetail = () => setView('detail');

  const saveEdit = async (updated: Memory, photos: File[]) => {
    if (editSaving) return;
    setEditSaving(true);
    try {
      let saved = await updateMemory(updated.id, {
        title: updated.title,
        story_text: updated.fullText,
        event_date: updated.eventDate,
      });
      if (photos.length > 0) {
        saved = await uploadMemoryPhotos(updated.id, photos);
      }
      const uiMemory = toUiMemory(saved);
      setMemories((prev) => prev.map((m) => (m.id === uiMemory.id ? uiMemory : m)));
      setSelected(uiMemory);
      setView('detail');
      setToastMessage('Воспоминание отредактировано');
      setToast(true);
      setTimeout(() => setToast(false), 2500);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Не удалось сохранить изменения');
    } finally {
      setEditSaving(false);
    }
  };

  const deletePhoto = async (assetId: string) => {
    if (!selected) return;
    const previousSelected = selected;
    const removeAsset = (memory: Memory) => ({
      ...memory,
      assets: memory.assets.filter((asset) => asset.id !== assetId),
    });
    const optimistic = removeAsset(selected);
    setSelected(optimistic);
    setMemories((prev) => prev.map((memory) => (
      memory.id === selected.id ? removeAsset(memory) : memory
    )));

    try {
      const saved = await deleteMemoryPhoto(selected.id, assetId);
      const uiMemory = toUiMemory(saved);
      setMemories((prev) => prev.map((m) => (m.id === uiMemory.id ? uiMemory : m)));
      setSelected(uiMemory);
      setToastMessage('Фотография удалена');
      setToast(true);
      setTimeout(() => setToast(false), 2500);
    } catch (error) {
      setSelected(previousSelected);
      setMemories((prev) => prev.map((memory) => (
        memory.id === previousSelected.id ? previousSelected : memory
      )));
      setLoadError(error instanceof Error ? error.message : 'Не удалось удалить фотографию');
      void getMemory(previousSelected.id)
        .then((memory) => {
          const uiMemory = toUiMemory(memory);
          setSelected(uiMemory);
          setMemories((prev) => prev.map((item) => (item.id === uiMemory.id ? uiMemory : item)));
        })
        .catch(() => undefined);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMemory(deleteTarget.id);
      setMemories((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setDeleteTarget(null);
      setView('list');
      setSelected(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Не удалось удалить воспоминание');
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {view === 'list' && (
        <HistoryList
          memories={memories}
          loading={userLoading || loading}
          error={userError || loadError}
          onOpen={openDetail}
          onRefresh={reload}
        />
      )}
      {view === 'detail' && selected && (
        <MemoryDetail
          memory={selected}
          onBack={backToList}
          onEdit={openEdit}
          onDelete={() => setDeleteTarget(selected)}
          onDeletePhoto={deletePhoto}
        />
      )}
      {view === 'edit' && selected && (
        <MemoryEdit
          memory={selected}
          onBack={backToDetail}
          onSave={saveEdit}
          onDeletePhoto={deletePhoto}
          saving={editSaving}
        />
      )}

      {/* Delete confirmation sheet */}
      {deleteTarget && (
        <DeleteConfirm
          title={deleteTarget.title}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {/* Success toast */}
      {toast && (
        <div
          style={{
            position: 'absolute', top: '72px', left: '16px', right: '16px', zIndex: 200,
            background: 'rgba(62,207,160,0.95)',
            borderRadius: '14px', padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: '10px',
            boxShadow: '0 8px 24px rgba(62,207,160,0.4)',
            animation: 'slideDown 0.3s ease',
          }}
        >
          <i className="ri-checkbox-circle-line" style={{ fontSize: '20px', color: '#fff' }} />
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{toastMessage}</span>
        </div>
      )}

      <style>{`@keyframes slideDown { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
}

/* ── History List ── */
function HistoryList({
  memories,
  loading,
  error,
  onOpen,
  onRefresh,
}: {
  memories: Memory[];
  loading: boolean;
  error: string | null;
  onOpen: (m: Memory) => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [search, setSearch] = useState('');
  const [filterEmotion, setFilterEmotion] = useState<string | null>(null);

  const EMOTION_FILTERS = [
    { label: 'Радость', color: '#3ECFA0' },
    { label: 'Печаль', color: '#4A90E2' },
    { label: 'Страх', color: '#8B7AB8' },
    { label: 'Гнев', color: '#FF6B6B' },
  ];

  const filtered = memories.filter((m) => {
    const matchSearch = m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.fullText.toLowerCase().includes(search.toLowerCase());
    const matchEmotion = filterEmotion ? m.dominantEmotion === filterEmotion : true;
    return matchSearch && matchEmotion;
  });

  return (
    <div
      style={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 20px 12px',
          background: 'rgba(26,27,46,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '-0.3px' }}>История</span>
          <span style={{ fontSize: '13px', color: '#6B7A94' }}>{memories.length} воспоминаний</span>
        </div>
        {/* Search */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', padding: '10px 14px',
          }}
        >
          <i className="ri-search-line" style={{ fontSize: '16px', color: '#6B7A94', flexShrink: 0 }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по воспоминаниям..."
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              fontSize: '14px', color: '#F5F5F7', caretColor: '#3ECFA0',
            }}
          />
        </div>
        {/* Emotion filters */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', overflowX: 'auto', paddingBottom: '2px' }}>
          <button
            onClick={() => setFilterEmotion(null)}
            style={{
              padding: '5px 14px', borderRadius: '99px', border: 'none', cursor: 'pointer',
              background: filterEmotion === null ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)',
              color: filterEmotion === null ? '#F5F5F7' : '#6B7A94',
              fontSize: '12px', fontWeight: 500, flexShrink: 0,
            }}
          >
            Все
          </button>
          {EMOTION_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setFilterEmotion(filterEmotion === f.label ? null : f.label)}
              style={{
                padding: '5px 14px', borderRadius: '99px', cursor: 'pointer',
                background: filterEmotion === f.label ? `${f.color}30` : 'rgba(255,255,255,0.06)',
                color: filterEmotion === f.label ? f.color : '#6B7A94',
                fontSize: '12px', fontWeight: 500, flexShrink: 0,
                border: filterEmotion === f.label ? `1px solid ${f.color}60` : '1px solid transparent',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <PullToRefresh
        onRefresh={onRefresh}
        disabled={loading}
        style={{ flex: 1, minHeight: 0 }}
        contentStyle={{ padding: '12px 16px 16px' }}
      >
        <div style={{ minHeight: '100%' }}>
          {loading ? (
            <InlineState icon="ri-loader-4-line" title="Загружаем историю" text="Собираем ваши воспоминания из Омута Памяти." />
          ) : error ? (
            <InlineState icon="ri-error-warning-line" title="Не удалось загрузить историю" text={error} tone="error" />
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filtered.map((m) => (
                <MemoryCard key={m.id} memory={m} onOpen={() => onOpen(m)} />
              ))}
            </div>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
}

function InlineState({
  icon,
  title,
  text,
  tone = 'default',
}: {
  icon: string;
  title: string;
  text: string;
  tone?: 'default' | 'error';
}) {
  const color = tone === 'error' ? '#FFB4B4' : '#3ECFA0';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '60px 32px', gap: '14px',
      }}
    >
      <div
        style={{
          width: '72px', height: '72px', borderRadius: '50%',
          background: `${color}1A`, border: `1.5px solid ${color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '4px',
        }}
      >
        <i className={icon} style={{ fontSize: '32px', color }} />
      </div>
      <p style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7', textAlign: 'center' }}>
        {title}
      </p>
      <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.6 }}>
        {text}
      </p>
    </div>
  );
}

function MemoryCard({ memory: m, onOpen }: { memory: Memory; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      style={{
        width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px',
        padding: '14px 16px', cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Emotion dot + icon */}
        <div
          style={{
            width: '42px', height: '42px', borderRadius: '50%', flexShrink: 0,
            background: `${m.dominantColor}20`,
            border: `1.5px solid ${m.dominantColor}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginTop: '1px',
          }}
        >
          <i className="ri-mic-fill" style={{ fontSize: '18px', color: m.dominantColor }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span
              style={{
                fontSize: '15px', fontWeight: 600, color: '#F5F5F7',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px',
              }}
            >
              {m.title}
            </span>
            <i className="ri-arrow-right-s-line" style={{ fontSize: '18px', color: '#4A5568', flexShrink: 0 }} />
          </div>

          <p
            style={{
              fontSize: '13px', color: '#A8B2C8', lineHeight: 1.5, marginBottom: '8px',
              overflow: 'hidden', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}
          >
            {m.preview}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className="ri-calendar-event-line" style={{ fontSize: '11px', color: '#6B7A94' }} />
              <span style={{ fontSize: '11px', color: '#6B7A94' }}>Событие: {m.happenedAt}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className="ri-save-line" style={{ fontSize: '11px', color: '#6B7A94' }} />
              <span style={{ fontSize: '11px', color: '#6B7A94' }}>Записано: {m.recordedAt}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Emotion mini-bars */}
      <div style={{ display: 'flex', gap: '4px', marginTop: '12px' }}>
        {m.emotions.map((e) => (
          <div key={e.label} style={{ flex: 1, height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${(e.value / 10) * 100}%`,
                background: e.gradient, borderRadius: '2px',
              }}
            />
          </div>
        ))}
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '60px 32px', gap: '14px',
      }}
    >
      <div
        style={{
          width: '72px', height: '72px', borderRadius: '50%',
          background: 'rgba(62,207,160,0.1)', border: '1.5px solid rgba(62,207,160,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '4px',
        }}
      >
        <i className="ri-book-open-line" style={{ fontSize: '32px', color: '#3ECFA0' }} />
      </div>
      <p style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7', textAlign: 'center' }}>
        Здесь будут собраны ваши воспоминания
      </p>
      <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.6 }}>
        Сохраните первое воспоминание, чтобы начать собирать свою личную историю.
      </p>
    </div>
  );
}

/* ── Memory Detail ── */
function MemoryDetail({
  memory: m, onBack, onEdit, onDelete, onDeletePhoto,
}: {
  memory: Memory;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDeletePhoto: (assetId: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState<MemoryAsset | null>(null);
  const [deletePhotoTarget, setDeletePhotoTarget] = useState<MemoryAsset | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const photos = m.assets.filter((asset) => asset.asset_type === 'photo');

  const confirmPhotoDelete = async () => {
    if (!deletePhotoTarget || deletingPhoto) return;
    setDeletingPhoto(true);
    try {
      await onDeletePhoto(deletePhotoTarget.id);
      if (viewingPhoto?.id === deletePhotoTarget.id) {
        setViewingPhoto(null);
      }
      setDeletePhotoTarget(null);
    } finally {
      setDeletingPhoto(false);
    }
  };

  return (
    <div
      style={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center',
          padding: '16px 20px 14px', gap: '12px',
          background: 'rgba(26,27,46,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <button
          onClick={onBack}
          style={{
            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.08)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          <i className="ri-arrow-left-s-line" style={{ fontSize: '20px', color: '#A8B2C8' }} />
        </button>
        <span style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '-0.2px' }}>
          Воспоминание
        </span>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 24px' }}>

        {/* Title + meta */}
        <div
          style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px', padding: '18px 18px', marginBottom: '14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div
              style={{
                width: '46px', height: '46px', borderRadius: '50%', flexShrink: 0,
                background: `${m.dominantColor}20`, border: `1.5px solid ${m.dominantColor}50`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <i className="ri-mic-fill" style={{ fontSize: '20px', color: m.dominantColor }} />
            </div>
            <div>
              <p style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F7', marginBottom: '6px', lineHeight: 1.3 }}>
                {m.title}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="ri-calendar-event-line" style={{ fontSize: '12px', color: '#6B7A94' }} />
                  <span style={{ fontSize: '12px', color: '#6B7A94' }}>Событие: {m.happenedAtFull}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="ri-save-line" style={{ fontSize: '12px', color: '#6B7A94' }} />
                  <span style={{ fontSize: '12px', color: '#6B7A94' }}>Записано: {m.recordedAt}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Emotions */}
        <div
          style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px', padding: '18px 18px', marginBottom: '14px',
          }}
        >
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', marginBottom: '16px' }}>
            Эмоциональный профиль
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {m.emotions.map((e) => (
              <div key={e.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#F5F5F7', minWidth: '56px' }}>{e.label}</span>
                <div style={{ flex: 1, height: '10px', borderRadius: '5px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%', width: `${(e.value / 10) * 100}%`,
                      background: e.gradient, borderRadius: '5px',
                      transition: 'width 0.8s ease-out',
                    }}
                  />
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: e.color, minWidth: '16px', textAlign: 'right' }}>
                  {e.value}
                </span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '11px', color: '#6B7A94', marginTop: '12px', textAlign: 'center' }}>
            Шкала от 1 до 10
          </p>
        </div>

        {photos.length > 0 && (
          <div
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '20px', padding: '18px 18px', marginBottom: '14px',
            }}
          >
            <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', marginBottom: '12px' }}>
              Фотографии
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {photos.map((asset) => (
                <div
                  key={asset.id}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.06)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setViewingPhoto(asset)}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  >
                    <img
                      src={assetUrl(asset)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </button>
                  <button
                    type="button"
                    aria-label="Удалить фото"
                    onClick={() => setDeletePhotoTarget(asset)}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(15,16,28,0.78)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
                    }}
                  >
                    <i className="ri-delete-bin-line" style={{ fontSize: '15px', color: '#FF6B6B' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expandable text */}
        <div
          style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px', overflow: 'hidden', marginBottom: '20px',
          }}
        >
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              width: '100%', padding: '16px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7' }}>
              Подробнее об этом событии
            </span>
            <div
              style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.25s ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="ri-arrow-down-s-line" style={{ fontSize: '18px', color: '#A8B2C8' }} />
            </div>
          </button>

          {expanded && (
            <div style={{ padding: '0 18px 18px' }}>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', marginBottom: '14px' }} />
              <p style={{ fontSize: '14px', color: '#C8D0E0', lineHeight: 1.75 }}>{m.fullText}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onEdit}
            style={{
              flex: 1, padding: '15px', borderRadius: '16px',
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              cursor: 'pointer',
            }}
          >
            <i className="ri-edit-line" style={{ fontSize: '18px', color: '#F5F5F7' }} />
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7' }}>Редактировать</span>
          </button>
          <button
            onClick={onDelete}
            style={{
              padding: '15px 18px', borderRadius: '16px',
              background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              cursor: 'pointer',
            }}
          >
            <i className="ri-delete-bin-line" style={{ fontSize: '18px', color: '#FF6B6B' }} />
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#FF6B6B' }}>Удалить</span>
          </button>
        </div>
      </div>
      {viewingPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setViewingPhoto(null)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 220,
            background: 'rgba(8,9,18,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setViewingPhoto(null);
            }}
            style={{
              position: 'absolute',
              top: 'calc(14px + env(safe-area-inset-top))',
              right: '16px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <i className="ri-close-line" style={{ fontSize: '22px', color: '#F5F5F7' }} />
          </button>
          <img
            src={assetUrl(viewingPhoto)}
            alt=""
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: '100%',
              maxHeight: '82vh',
              objectFit: 'contain',
              borderRadius: '16px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            }}
          />
        </div>
      )}
      {deletePhotoTarget && (
        <PhotoDeleteConfirm
          deleting={deletingPhoto}
          onCancel={() => setDeletePhotoTarget(null)}
          onConfirm={confirmPhotoDelete}
        />
      )}
    </div>
  );
}

/* ── Memory Edit ── */
function MemoryEdit({
  memory: m, onBack, onSave, onDeletePhoto, saving,
}: {
  memory: Memory;
  onBack: () => void;
  onSave: (updated: Memory, photos: File[]) => void;
  onDeletePhoto: (assetId: string) => Promise<void>;
  saving: boolean;
}) {
  const [title, setTitle] = useState(m.title);
  const [eventDate, setEventDate] = useState(m.eventDate);
  const [text, setText] = useState(m.fullText);
  const [photos, setPhotos] = useState<File[]>([]);
  const [deletePhotoTarget, setDeletePhotoTarget] = useState<MemoryAsset | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const existingPhotos = m.assets.filter((asset) => asset.asset_type === 'photo');
  const existingPhotoCount = existingPhotos.length;
  const availablePhotoSlots = Math.max(0, 10 - existingPhotoCount);

  const handleSave = () => {
    if (saving) return;
    onSave({
      ...m,
      title,
      eventDate,
      fullText: text,
      preview: text.slice(0, 60) + '...',
    }, photos);
  };

  const confirmPhotoDelete = async () => {
    if (!deletePhotoTarget || deletingPhoto) return;
    setDeletingPhoto(true);
    try {
      await onDeletePhoto(deletePhotoTarget.id);
      setDeletePhotoTarget(null);
    } finally {
      setDeletingPhoto(false);
    }
  };

  return (
    <div
      style={{
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px 14px',
          background: 'rgba(26,27,46,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <button
          onClick={onBack}
          style={{
            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.08)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          <i className="ri-arrow-left-s-line" style={{ fontSize: '20px', color: '#A8B2C8' }} />
        </button>
        <span style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '-0.2px' }}>
          Редактировать
        </span>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '7px 16px', borderRadius: '10px', border: 'none',
            background: saving ? 'rgba(255,255,255,0.12)' : 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
            color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            boxShadow: saving ? 'none' : '0 4px 12px rgba(62,207,160,0.3)',
            opacity: saving ? 0.75 : 1,
          }}
        >
          {saving ? 'Сохраняем...' : 'Сохранить'}
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
        {/* Title field */}
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '8px' }}>
          Название
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{
            width: '100%', padding: '14px 16px', borderRadius: '14px',
            background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)',
            color: '#F5F5F7', fontSize: '16px', fontWeight: 600, outline: 'none',
            caretColor: '#3ECFA0', marginBottom: '20px', boxSizing: 'border-box',
          }}
        />

        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '8px' }}>
          Дата события
        </label>
        <input
          type="date"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
          style={{
            width: '100%', padding: '14px 16px', borderRadius: '14px',
            background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)',
            color: '#F5F5F7', fontSize: '15px', outline: 'none',
            caretColor: '#3ECFA0', marginBottom: '20px', boxSizing: 'border-box',
            colorScheme: 'dark',
          }}
        />

        {/* Text field */}
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '8px' }}>
          Воспоминание
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          maxLength={500}
          style={{
            width: '100%', padding: '14px 16px', borderRadius: '14px',
            background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)',
            color: '#C8D0E0', fontSize: '15px', lineHeight: 1.7, outline: 'none',
            caretColor: '#3ECFA0', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />
        <p style={{ fontSize: '12px', color: '#4A5568', textAlign: 'right', marginTop: '6px' }}>
          {text.length} / 500
        </p>

        <div style={{ marginTop: '22px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: '8px' }}>
            Фотографии
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => {
              setPhotos(Array.from(event.target.files ?? []).slice(0, availablePhotoSlots));
              event.target.value = '';
            }}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={saving || availablePhotoSlots === 0}
            style={{
              width: '100%', padding: '14px 16px', borderRadius: '14px',
              background: 'rgba(139,122,184,0.12)', border: '1.5px dashed rgba(139,122,184,0.38)',
              color: '#C8D0E0', fontSize: '15px', fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              cursor: saving || availablePhotoSlots === 0 ? 'default' : 'pointer', marginBottom: '12px',
              opacity: saving || availablePhotoSlots === 0 ? 0.6 : 1,
            }}
          >
            <i className="ri-image-add-line" style={{ fontSize: '20px', color: '#8B7AB8' }} />
            {availablePhotoSlots === 0 ? 'Лимит фотографий достигнут' : 'Подгрузить фотографии'}
          </button>
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
              {photos.map((photo) => (
                <div
                  key={`${photo.name}-${photo.size}-${photo.lastModified}`}
                  style={{
                    flexShrink: 0,
                    width: '74px',
                    height: '74px',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    color: '#A8B2C8',
                    fontSize: '11px',
                    textAlign: 'center',
                    lineHeight: 1.25,
                    overflow: 'hidden',
                  }}
                >
                  {photo.name}
                </div>
              ))}
            </div>
          )}
          {existingPhotos.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
              {existingPhotos.map((asset) => (
                <div
                  key={asset.id}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.06)',
                  }}
                >
                  <img src={assetUrl(asset)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    aria-label="Удалить фото"
                    disabled={saving || deletingPhoto}
                    onClick={() => setDeletePhotoTarget(asset)}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(15,16,28,0.78)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: saving || deletingPhoto ? 'default' : 'pointer',
                      opacity: saving || deletingPhoto ? 0.55 : 1,
                      boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
                    }}
                  >
                    <i className="ri-delete-bin-line" style={{ fontSize: '15px', color: '#FF6B6B' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {deletePhotoTarget && (
        <PhotoDeleteConfirm
          deleting={deletingPhoto}
          onCancel={() => setDeletePhotoTarget(null)}
          onConfirm={confirmPhotoDelete}
        />
      )}
    </div>
  );
}

function PhotoDeleteConfirm({
  deleting,
  onCancel,
  onConfirm,
}: {
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <div
        onClick={deleting ? undefined : onCancel}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)', zIndex: 230,
        }}
      />
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 231,
          background: 'linear-gradient(180deg, #232438 0%, #1E1F35 100%)',
          borderRadius: '24px 24px 0 0',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          padding: '28px 20px calc(36px + env(safe-area-inset-bottom))',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.2)' }} />
        </div>
        <div
          style={{
            width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 16px',
            background: 'rgba(255,107,107,0.15)', border: '1.5px solid rgba(255,107,107,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ri-image-line" style={{ fontSize: '25px', color: '#FF6B6B' }} />
        </div>
        <p style={{ fontSize: '19px', fontWeight: 700, color: '#F5F5F7', textAlign: 'center', marginBottom: '8px' }}>
          Удалить фотографию?
        </p>
        <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.6, marginBottom: '28px' }}>
          Фотография будет удалена из этого воспоминания без возможности восстановления.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              width: '100%', padding: '16px', borderRadius: '16px', border: 'none',
              background: deleting ? 'rgba(255,255,255,0.12)' : 'linear-gradient(135deg, #FF6B6B, #EE5A52)',
              color: '#fff', fontSize: '16px', fontWeight: 600,
              cursor: deleting ? 'default' : 'pointer',
              boxShadow: deleting ? 'none' : '0 8px 24px rgba(255,107,107,0.35)',
            }}
          >
            {deleting ? 'Удаляем...' : 'Удалить фото'}
          </button>
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{
              width: '100%', padding: '15px', borderRadius: '16px',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#A8B2C8', fontSize: '15px', fontWeight: 500,
              cursor: deleting ? 'default' : 'pointer',
              opacity: deleting ? 0.55 : 1,
            }}
          >
            Отмена
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Delete Confirm ── */
function DeleteConfirm({ title, onCancel, onConfirm }: { title: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <>
      <div
        onClick={onCancel}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)', zIndex: 200,
        }}
      />
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 201,
          background: 'linear-gradient(180deg, #232438 0%, #1E1F35 100%)',
          borderRadius: '24px 24px 0 0',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          padding: '28px 20px calc(36px + env(safe-area-inset-bottom))',
        }}
      >
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.2)' }} />
        </div>

        <div
          style={{
            width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 16px',
            background: 'rgba(255,107,107,0.15)', border: '1.5px solid rgba(255,107,107,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ri-delete-bin-line" style={{ fontSize: '26px', color: '#FF6B6B' }} />
        </div>

        <p style={{ fontSize: '19px', fontWeight: 700, color: '#F5F5F7', textAlign: 'center', marginBottom: '8px' }}>
          Удалить воспоминание?
        </p>
        <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.6, marginBottom: '28px' }}>
          Вы точно хотите удалить событие{'\n'}«{title}»? Это действие нельзя отменить.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={onConfirm}
            style={{
              width: '100%', padding: '16px', borderRadius: '16px', border: 'none',
              background: 'linear-gradient(135deg, #FF6B6B, #EE5A52)',
              color: '#fff', fontSize: '16px', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(255,107,107,0.35)',
            }}
          >
            Удалить
          </button>
          <button
            onClick={onCancel}
            style={{
              width: '100%', padding: '15px', borderRadius: '16px',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#A8B2C8', fontSize: '15px', fontWeight: 500, cursor: 'pointer',
            }}
          >
            Отмена
          </button>
        </div>
      </div>
    </>
  );
}
